// components/pagination-container.tsx
"use client";

import { useSearchParams } from "next/navigation";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../base/pagination";

const MAX_VISIBLE_PAGES = 5;

export function Paginator(props: {
  totalNbOfItems: number;
  nbOfItemsPerPage: number;
}) {
  const searchParams = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const nbOfPages =
    props.nbOfItemsPerPage <= 0
      ? 1
      : Math.ceil(props.totalNbOfItems / props.nbOfItemsPerPage);

  if (nbOfPages <= 1) {
    return null;
  }

  // Helper function to generate the full URL for any given page number
  const createPageUrl = (pageNumber: number): string => {
    const params = new URLSearchParams(searchParams.toString());
    const newPage = Math.max(1, Math.min(pageNumber, nbOfPages));

    params.set("page", newPage.toString());

    // Returns the relative URL (e.g., "?page=3&sort=price")
    return `?${params.toString()}`;
  };

  // --- Pagination Range Logic ---
  const halfVisible = Math.floor(MAX_VISIBLE_PAGES / 2);

  let startPage = Math.max(1, currentPage - halfVisible);
  let endPage = Math.min(nbOfPages, currentPage + halfVisible);

  // Adjust range logic for consistency at boundaries
  if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
    if (startPage > 1) {
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    } else if (endPage < nbOfPages) {
      endPage = Math.min(nbOfPages, startPage + MAX_VISIBLE_PAGES - 1);
    }
  }

  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i,
  );

  return (
    <Pagination className="py-4">
      <PaginationContent>
        {/* Previous Button */}
        <PaginationItem>
          <PaginationPrevious
            href={createPageUrl(currentPage - 1)}
            aria-disabled={currentPage === 1}
            tabIndex={currentPage === 1 ? -1 : undefined}
            className={
              currentPage === 1 ? "pointer-events-none opacity-50" : undefined
            }
          />
        </PaginationItem>

        {/* Start Ellipsis */}
        {startPage > 1 && (
          <>
            <PaginationItem>
              <PaginationLink href={createPageUrl(1)}>1</PaginationLink>
            </PaginationItem>
            {startPage > 2 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
          </>
        )}

        {/* Visible Page Numbers */}
        {pageNumbers.map((page) => (
          <PaginationItem key={page}>
            <PaginationLink
              // CRITICAL CHANGE: Use href directly
              href={createPageUrl(page)}
              isActive={page === currentPage}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {/* End Ellipsis */}
        {endPage < nbOfPages && (
          <>
            {endPage < nbOfPages - 1 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink href={createPageUrl(nbOfPages)}>
                {nbOfPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        {/* Next Button */}
        <PaginationItem>
          <PaginationNext
            href={createPageUrl(currentPage + 1)}
            aria-disabled={currentPage === nbOfPages}
            tabIndex={currentPage === nbOfPages ? -1 : undefined}
            className={
              currentPage === nbOfPages
                ? "pointer-events-none opacity-50"
                : undefined
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
